import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, ShoppingCart, Loader2, Mail, Send, Eye, EyeOff, Copy, Check, Users } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { createOrder, sendOrderPreviewEmail } from '../services/orders.services';
import { fetchLaundryProducts } from '@/pages/laundry/services/laundryProducts.services';
import { fetchProviders } from '@/pages/parametrizacion/providers/Services/providers.services';
import { useAuth } from '@/contexts/AuthContext';
import { fetchAdminUsers } from '@/pages/reports/services/adminUsers.reports.services';

const OrderModal = ({ open, onOpenChange, idLavanderia, laundryName = '', onCreated }) => {
  const { toast } = useToast();
  const { user } = useAuth();

  // Usuarios admin para CC
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [selectedAdminIds, setSelectedAdminIds] = useState([]);

  // Productos de la lavandería (laundry_products)
  const [laundryProducts, setLaundryProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [providerEmailMap, setProviderEmailMap] = useState({});

  // Items del pedido
  const [items, setItems] = useState([{ id: 1, productoId: '', cantidad: '' }]);
  const [observaciones, setObservaciones] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Cargar productos de la lavandería (lista de precios)
  const loadLaundryProducts = useCallback(async () => {
    if (!idLavanderia) return;
    setLoadingProducts(true);
    try {
      const res = await fetchLaundryProducts({ lavanderiaId: idLavanderia, onlyActive: true });
      const list = Array.isArray(res?.data) ? res.data : [];
      setLaundryProducts(list);
    } catch (e) {
      console.error('Error cargando productos del centro:', e);
      setLaundryProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, [idLavanderia]);

  // Cargar usuarios admin
  const loadAdminUsers = useCallback(async () => {
    setLoadingAdmins(true);
    try {
      const res = await fetchAdminUsers({ estado: 'Activo' });
      setAdminUsers(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.error('Error cargando admins:', e);
      setAdminUsers([]);
    } finally {
      setLoadingAdmins(false);
    }
  }, []);

  // Cargar correos de proveedores (para envío de email)
  const loadProviderEmails = useCallback(async () => {
    try {
      const res = await fetchProviders({ limit: 500, offset: 1, query: { estado: 'Activo' } });
      const list = Array.isArray(res?.data) ? res.data : [];
      const map = {};
      list.forEach(p => { map[p._id || p.id] = p.correo || ''; });
      setProviderEmailMap(map);
    } catch (e) {
      console.error('Error cargando correos proveedores:', e);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadLaundryProducts();
      loadProviderEmails();
      loadAdminUsers();
    } else {
      setSelectedProviderId('');
      setItems([{ id: 1, productoId: '', cantidad: '' }]);
      setObservaciones('');
      setSubmitting(false);
      setSelectedAdminIds([]);
      setShowPreview(false);
    }
  }, [open, loadLaundryProducts, loadProviderEmails, loadAdminUsers]);

  // Resetear items cuando cambia el proveedor
  useEffect(() => {
    setItems([{ id: 1, productoId: '', cantidad: '' }]);
  }, [selectedProviderId]);

  // Proveedores únicos extraídos de laundry_products + correo del catálogo
  const providers = useMemo(() => {
    const map = new Map();
    laundryProducts.forEach(lp => {
      const prov = lp?.proveedor;
      if (prov?.id && !map.has(prov.id)) {
        map.set(prov.id, {
          id: prov.id,
          nombre: prov.nombre || '',
          correo: providerEmailMap[prov.id] || prov.correo || '',
        });
      }
    });
    return Array.from(map.values());
  }, [laundryProducts, providerEmailMap]);

  // Proveedor seleccionado
  const selectedProvider = useMemo(() => providers.find(p => p.id === selectedProviderId) || null, [providers, selectedProviderId]);

  // Productos filtrados por proveedor seleccionado (desde laundry_products)
  const filteredProducts = useMemo(() => {
    if (!selectedProviderId) return [];
    return laundryProducts
      .filter(lp => lp?.proveedor?.id === selectedProviderId)
      .map(lp => ({
        _id: lp?.producto?.id || lp?.producto?._id || lp?._id,
        nombre: lp?.producto?.nombre || '',
        presentacionLitros: Number(lp?.presentacionLitros ?? 0),
        costo: Number(lp?.costo ?? 0),
        stockMinimo: Number(lp?.stockMinimo ?? 0),
      }));
  }, [laundryProducts, selectedProviderId]);

  const getProduct = (pid) => filteredProducts.find(p => (p._id || p.id) === pid) || null;

  const computeLitros = (pid, qty) => {
    const prod = getProduct(pid);
    const pres = Number(prod?.presentacionLitros ?? 0) || 0;
    return (Number(qty) || 0) * pres;
  };

  const addRow = () => {
    setItems(prev => [...prev, { id: (prev[prev.length - 1]?.id || 0) + 1, productoId: '', cantidad: '' }]);
  };

  const removeRow = (id) => {
    setItems(prev => (prev.length > 1 ? prev.filter(it => it.id !== id) : prev));
  };

  const updateRow = (id, patch) => {
    setItems(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));
  };

  // Totales
  const validItems = useMemo(() => items.filter(it => it.productoId && Number(it.cantidad) > 0), [items]);
  const totalLitros = useMemo(() => validItems.reduce((sum, it) => sum + computeLitros(it.productoId, it.cantidad), 0), [validItems]);
  const totalProductos = validItems.length;

  // ── Email helpers ──
  const userName = user?.name || 'Usuario';

  // CC: construir string de correos de admins seleccionados
  const selectedAdmins = useMemo(() => adminUsers.filter(a => selectedAdminIds.includes(a._id || a.id)), [adminUsers, selectedAdminIds]);
  const ccEmails = useMemo(() => selectedAdmins.map(a => a.correo).filter(Boolean).join(','), [selectedAdmins]);

  const toggleAdmin = (adminId) => {
    setSelectedAdminIds(prev =>
      prev.includes(adminId) ? prev.filter(id => id !== adminId) : [...prev, adminId]
    );
  };

  const buildEmailBody = useCallback(() => {
    const centro = laundryName || 'Centro de lavado';
    const proveedor = selectedProvider?.nombre || '';
    const fecha = new Date().toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' });
    const lines = [];
    lines.push(`Estimado(a) ${proveedor},`);
    lines.push('');
    lines.push(`Desde ${centro} solicitamos el siguiente pedido de productos:`);
    lines.push('');
    validItems.forEach((it, idx) => {
      const prod = getProduct(it.productoId);
      const litros = computeLitros(it.productoId, it.cantidad);
      const nombre = prod?.nombre || '';
      const unidad = prod?.unidad || 'und';
      lines.push(`  ${idx + 1}. ${nombre} - Cantidad: ${Number(it.cantidad)} ${unidad} - Litros: ${Number.isFinite(litros) ? litros.toLocaleString() : 0}`);
    });
    lines.push('');
    lines.push(`Total productos: ${totalProductos}`);
    lines.push(`Total litros: ${totalLitros.toLocaleString()}`);
    if (observaciones) {
      lines.push('');
      lines.push(`Observaciones: ${observaciones}`);
    }
    lines.push('');
    lines.push(`Fecha: ${fecha}`);
    lines.push(`Solicitado por: ${userName}`);
    lines.push('');
    lines.push('Quedamos atentos a su confirmacion.');
    lines.push('Cordialmente.');
    return lines.join('\n');
  }, [validItems, selectedProvider, laundryName, observaciones, totalProductos, totalLitros, userName]);

  const handleOpenInEmail = () => {
    if (validItems.length === 0) {
      toast({ title: 'Sin productos', description: 'Agrega al menos un producto con cantidad valida.', variant: 'destructive' });
      return;
    }
    const to = selectedProvider?.correo || '';
    const subject = `Pedido de productos - ${laundryName || 'Centro de lavado'}`;
    const body = buildEmailBody();
    let url = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    if (ccEmails) url += `&cc=${encodeURIComponent(ccEmails)}`;
    window.location.href = url;
  };

  const handleSendEmail = async () => {
    if (!selectedProvider?.correo) {
      toast({ title: 'Sin correo', description: 'El proveedor no tiene correo registrado.', variant: 'destructive' });
      return;
    }
    if (validItems.length === 0) {
      toast({ title: 'Sin productos', description: 'Agrega al menos un producto con cantidad válida.', variant: 'destructive' });
      return;
    }
    try {
      setSendingEmail(true);
      const itemsSummary = validItems.map(it => {
        const prod = getProduct(it.productoId);
        const litros = computeLitros(it.productoId, it.cantidad);
        return { productoNombre: prod?.nombre || '', cantidad: Number(it.cantidad), litrosTotal: Number.isFinite(litros) ? litros : 0 };
      });
      await sendOrderPreviewEmail({
        to: selectedProvider.correo,
        cc: ccEmails || undefined,
        lavanderiaNombre: laundryName || 'Centro de lavado',
        proveedorNombre: selectedProvider.nombre,
        solicitadoPor: userName,
        fecha: new Date().toISOString(),
        items: itemsSummary,
        observaciones: observaciones || undefined,
      });
      toast({ title: 'Correo enviado', description: `Resumen enviado a ${selectedProvider.correo}` });
    } catch (error) {
      console.error('Error enviando correo:', error);
      toast({ title: 'Error', description: error.message || 'No se pudo enviar el correo.', variant: 'destructive' });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProvider) {
      toast({ title: 'Proveedor requerido', description: 'Selecciona un proveedor.', variant: 'destructive' });
      return;
    }
    if (validItems.length === 0) {
      toast({ title: 'Productos requeridos', description: 'Agrega al menos un producto con cantidad válida.', variant: 'destructive' });
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        fecha: new Date().toISOString(),
        lavanderia: { id: idLavanderia, nombre: laundryName || 'Centro de lavado' },
        proveedor: { id: selectedProvider.id, nombre: selectedProvider.nombre },
        items: validItems.map(it => {
          const prod = getProduct(it.productoId);
          return {
            producto: { id: it.productoId, nombre: prod?.nombre || '' },
            cantidad: Number(it.cantidad),
          };
        }),
        observaciones: observaciones || undefined,
      };
      const res = await createOrder(payload);
      const numero = res?.data?.numeroPedido || '';
      toast({ title: 'Pedido creado', description: `Pedido ${numero ? '#' + numero + ' ' : ''}con ${validItems.length} producto(s) para ${selectedProvider.nombre}` });
      onOpenChange(false);
      onCreated && onCreated(res?.data);
    } catch (error) {
      console.error('Error creando pedido:', error);
      const message = error?.response?.data?.message || error.message || 'No se pudo crear el pedido.';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-amber-600" />
            Hacer pedido a proveedor
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto space-y-5 pr-2">
            {/* Centro de lavado (info) */}
            <div className="rounded-lg border border-border p-3 bg-muted/30">
              <p className="text-sm font-medium text-muted-foreground mb-1">Centro de lavado</p>
              <p className="text-base font-semibold text-foreground">{laundryName || 'Centro actual'}</p>
            </div>

            {/* Selección de proveedor */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Proveedor</label>
              <select
                value={selectedProviderId}
                onChange={(e) => setSelectedProviderId(e.target.value)}
                className="w-full border border-input rounded-md p-2 h-10 bg-background text-foreground focus:border-ring focus:ring-1 focus:ring-ring"
                disabled={loadingProducts}
                required
              >
                <option value="" disabled>
                  {loadingProducts ? 'Cargando proveedores...' : 'Selecciona un proveedor'}
                </option>
                {providers.map(p => (
                  <option key={p.id} value={p.id}>{p.nombre}</option>
                ))}
              </select>
              {selectedProvider?.correo && (
                <p className="text-xs text-muted-foreground mt-1">Correo: {selectedProvider.correo}</p>
              )}
            </div>

            {/* Productos a pedir */}
            {selectedProviderId && (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Productos a pedir</p>
                {loadingProducts ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                    <Loader2 className="h-4 w-4 animate-spin" /> Cargando productos...
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4">No hay productos en la lista de precios de este centro para este proveedor.</p>
                ) : (
                  <div className="space-y-3">
                    {items.map((row) => {
                      const prod = getProduct(row.productoId);
                      const litros = computeLitros(row.productoId, row.cantidad);
                      return (
                        <div key={row.id} className="rounded-lg border border-border p-3 bg-card">
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                            <div className="md:col-span-5">
                              <label className="block text-sm font-medium text-foreground mb-1">Producto</label>
                              <select
                                value={row.productoId}
                                onChange={(e) => updateRow(row.id, { productoId: e.target.value, cantidad: '' })}
                                className="w-full border border-input rounded-md p-2 h-10 bg-background text-foreground focus:border-ring focus:ring-1 focus:ring-ring"
                                required
                              >
                                <option value="" disabled>Selecciona un producto</option>
                                {filteredProducts.map(p => (
                                  <option key={p._id || p.id} value={p._id || p.id}>
                                    {p.nombre}{p.unidad ? ` (${p.unidad})` : ''}
                                  </option>
                                ))}
                              </select>
                              {prod && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  Presentación: {Number(prod?.presentacionLitros ?? 0)} {prod?.unidad || 'und'}
                                  {prod?.costo ? ` · Costo: $${Number(prod.costo).toLocaleString()}` : ''}
                                </div>
                              )}
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-foreground mb-1">Cantidad</label>
                              <Input
                                type="number"
                                value={row.cantidad}
                                onChange={(e) => updateRow(row.id, { cantidad: e.target.value })}
                                min="0"
                                step="0.01"
                                required
                                className="h-10"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-foreground mb-1">Litros</label>
                              <div className="h-10 flex items-center px-3 border border-input rounded-md text-sm bg-muted/50 text-foreground">
                                {Number.isFinite(litros) ? litros.toLocaleString() : 0}
                              </div>
                            </div>
                            <div className="md:col-span-3 flex items-end gap-1">
                              <div>
                                <label className="block text-sm font-medium mb-1 opacity-0 select-none">Acc</label>
                                <div className="h-10 flex items-center gap-1">
                                  <Button type="button" variant="outline" size="icon" onClick={addRow} aria-label="Agregar línea" className="shrink-0">
                                    <Plus className="h-4 w-4" />
                                  </Button>
                                  <Button type="button" variant="ghost" size="icon" onClick={() => removeRow(row.id)} disabled={items.length <= 1} aria-label="Quitar línea" className="shrink-0">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Resumen */}
                {validItems.length > 0 && (
                  <div className="mt-3 rounded-lg border border-border p-3 bg-muted/30 flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{totalProductos} producto(s)</span>
                    <span className="text-sm font-semibold text-foreground">Total litros: {totalLitros.toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}

            {/* Enviar correo al proveedor */}
            {selectedProvider && validItems.length > 0 && (
              <div className="rounded-lg border border-border p-3 bg-muted/10">
                <p className="text-sm font-medium text-foreground mb-2 flex items-center gap-1.5">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Enviar pedido al proveedor
                </p>
                <div className="mb-3">
                  <label className="block text-xs text-muted-foreground mb-1">Destinatario</label>
                  <div className="h-9 flex items-center px-3 border border-input rounded-md text-sm bg-muted/50 text-foreground">
                    {selectedProvider.correo || 'Sin correo registrado'}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="block text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" /> Enviar copia a (CC)
                  </label>
                  {loadingAdmins ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Cargando usuarios...
                    </div>
                  ) : adminUsers.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-1">No hay usuarios admin registrados.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {adminUsers.map(admin => {
                        const aid = admin._id || admin.id;
                        const isSelected = selectedAdminIds.includes(aid);
                        return (
                          <button
                            key={aid}
                            type="button"
                            onClick={() => toggleAdmin(aid)}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                              isSelected
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-muted/50 text-muted-foreground border-input hover:bg-muted'
                            }`}
                          >
                            {isSelected && <Check className="h-3 w-3" />}
                            {admin.nombre || admin.correo}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {selectedAdmins.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1.5">
                      CC: {selectedAdmins.map(a => a.correo).filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button type="button" variant="outline" size="sm" onClick={handleOpenInEmail} disabled={validItems.length === 0}>
                    <Mail className="h-4 w-4 mr-1.5" /> Abrir en correo
                  </Button>
                  <Button type="button" variant="secondary" size="sm" onClick={handleSendEmail} disabled={sendingEmail || !selectedProvider.correo || validItems.length === 0}>
                    {sendingEmail ? (
                      <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Enviando...</>
                    ) : (
                      <><Send className="h-4 w-4 mr-1.5" />Enviar por servidor</>
                    )}
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setShowPreview(prev => !prev)}>
                    {showPreview ? <><EyeOff className="h-4 w-4 mr-1.5" />Ocultar resumen</> : <><Eye className="h-4 w-4 mr-1.5" />Ver resumen</>}
                  </Button>
                </div>
                {showPreview && (
                  <div className="mt-3 relative">
                    <div className="absolute top-2 right-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => {
                          navigator.clipboard.writeText(buildEmailBody());
                          toast({ title: 'Copiado', description: 'Resumen copiado al portapapeles.' });
                        }}
                        aria-label="Copiar resumen"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <pre className="whitespace-pre-wrap text-xs text-foreground bg-muted/50 border border-input rounded-md p-3 pr-10 max-h-[200px] overflow-y-auto font-sans">{buildEmailBody()}</pre>
                  </div>
                )}
              </div>
            )}

            {/* Observaciones */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Observaciones</label>
              <textarea
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Opcional"
                className="w-full border border-input rounded-md p-2 min-h-[80px] bg-background text-foreground focus:border-ring focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting || !selectedProviderId || validItems.length === 0}>
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creando pedido...</>
              ) : (
                'Crear pedido'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OrderModal;
